import type { HandFrame, HandSample } from '../domain/types'
import { extendedFingerFlags } from './fingerCount'

type Options = { video:HTMLVideoElement; stage:HTMLCanvasElement; panel:HTMLElement; isOpen:()=>boolean; toggle:()=>void; isModal:()=>boolean; isGuide:()=>boolean }
type Point = { x:number; y:number }
type Drag = { kind:'button'|'slider'|'scroll'|'step'; target:HTMLElement|null; x:number; y:number; scroll:number; moved:boolean }
/** One consumer of the existing tracker. A pinch captures one action until release. */
export class HandMenu {
  private cursor=document.createElement('div')
  private selected:HTMLElement|null=null
  private owner:Point|null=null
  private positionNow:Point|null=null
  private lastAt=0
  private cornerAt:number|null=null
  private cornerLatched=false
  private armed=false
  private pinchAt:number|null=null
  private drag:Drag|null=null
  readonly trace={frames:0,visibleHands:0,opens:0,clicks:0,sliders:0,scrolls:0,lastTarget:''}
  constructor(private options:Options){
    this.cursor.className='hand-menu-cursor';this.cursor.hidden=true;this.cursor.setAttribute('aria-hidden','true');document.body.append(this.cursor)
  }
  reset(){
    this.armed=false;this.pinchAt=null;this.drag=null;this.owner=null;this.positionNow=null;this.lastAt=0
    this.selected?.classList.remove('hand-hover');this.selected=null;this.cursor.hidden=true;this.cornerAt=null;this.cornerLatched=false
    this.cursor.dataset.action='idle'
  }
  dispose(){this.reset();this.cursor.remove()}
  push(frame:HandFrame):boolean {
    this.trace.frames++
    const hands=frame.hands.filter(h=>h.confidence>=.65&&h.landmarks.length>=21)
    if(!hands.length){this.reset();return this.blocking()}
    if(this.lastAt&&frame.timestampMs-this.lastAt>220)this.reset()
    // Proximity keeps ownership stable through handedness flicker and result reordering.
    const previous=this.owner
    const hand=previous?[...hands].sort((a,b)=>distance(a.landmarks[8]!,previous)-distance(b.landmarks[8]!,previous))[0]!:hands[0]!
    const raw=hand.landmarks[8]!
    if(previous&&distance(raw,previous)>.24)this.reset()
    this.owner={x:raw.x,y:raw.y};this.trace.visibleHands++
    const dt=this.lastAt?Math.min(80,Math.max(1,frame.timestampMs-this.lastAt)):33;this.lastAt=frame.timestampMs
    const point=this.position(hand)
    const old=this.positionNow??point
    const speed=distance(point,old)/dt
    const blend=1-Math.exp(-dt/(speed>.002?12:28))
    this.positionNow={x:old.x+(point.x-old.x)*blend,y:old.y+(point.y-old.y)*blend}
    const x=this.positionNow.x*innerWidth,y=this.positionNow.y*innerHeight
    const palm=distance(hand.landmarks[5]!,hand.landmarks[17]!)
    const pinch=distance(hand.landmarks[4]!,raw)/Math.max(.03,palm)
    const inCorner=point.x>.8&&point.y<.2&&pinch>.5&&Object.values(extendedFingerFlags(hand)).some(Boolean)
    if(!inCorner){this.cornerAt=null;this.cornerLatched=false}
    else if(!this.cornerLatched&&!this.drag){
      this.cornerAt??=frame.timestampMs
      const progress=Math.min(1,(frame.timestampMs-this.cornerAt)/1000)
      this.cursor.style.setProperty('--hold',`${progress*360}deg`)
      if(progress===1){this.cornerLatched=true;this.cornerAt=null;this.armed=false;this.pinchAt=null;this.options.toggle();this.trace.opens++}
    }
    const blocking=this.blocking(),open=blocking||this.options.isGuide()
    this.cursor.hidden=!open&&!inCorner;this.cursor.style.transform=`translate(${x}px,${y}px)`
    if(!inCorner)this.cursor.style.setProperty('--hold','0deg')
    if(!open){this.armed=false;this.drag=null;return inCorner}
    const surface=document.elementFromPoint(x,y)
    const hit=surface?.closest<HTMLElement>('button,a,input,select')??null
    const target=hit&&this.allowed(hit)?hit:null
    if(target!==this.selected){this.selected?.classList.remove('hand-hover');this.selected=target;target?.classList.add('hand-hover')}
    if(pinch>.5){
      if(this.drag?.kind==='button'&&frame.timestampMs-(this.pinchAt??frame.timestampMs)>=60&&!this.drag.moved&&target===this.drag.target&&target&&this.allowed(target)){
        target.click();this.trace.clicks++
      }
      this.armed=true;this.pinchAt=null;this.drag=null;this.cursor.dataset.action='idle'
    }else if(pinch<.3&&this.armed&&!this.drag){
      // Capture the start surface immediately; crossing a control during a scroll cannot activate it.
      const inPanel=Boolean(surface&&this.options.panel.contains(surface))
      const kind=target instanceof HTMLInputElement&&target.type==='range'?'slider':target?.dataset.handScroll?'step':target?'button':inPanel?'scroll':null
      this.armed=false;this.pinchAt=frame.timestampMs
      if(kind){this.drag={kind,target,x,y,scroll:this.options.panel.scrollTop,moved:false};this.trace.lastTarget=target?.id??'panel-space';this.cursor.dataset.action=kind;if(kind==='slider')this.trace.sliders++}
    }
    const drag=this.drag
    if(drag){
      if(Math.hypot(x-drag.x,y-drag.y)>12)drag.moved=true
      if(drag.kind==='button'&&Math.abs(y-drag.y)>12&&Math.abs(y-drag.y)>Math.abs(x-drag.x)*1.2&&drag.target&&this.options.panel.contains(drag.target)&&this.options.panel.scrollHeight>this.options.panel.clientHeight){drag.kind='scroll';drag.target=null;this.cursor.dataset.action='scroll'}
      const held=frame.timestampMs-(this.pinchAt??frame.timestampMs)>=60
      if(held&&drag.kind==='slider'&&drag.target instanceof HTMLInputElement){
        const slider=drag.target,rect=slider.getBoundingClientRect(),low=Number(slider.min||0),high=Number(slider.max||100),step=Number(slider.step)||1
        const value=low+Math.max(0,Math.min(1,(x-rect.left)/Math.max(1,rect.width)))*(high-low)
        slider.value=String(low+Math.round((value-low)/step)*step);slider.dispatchEvent(new Event('input',{bubbles:true}))
      }
      if(held&&drag.kind==='scroll'){this.options.panel.scrollTop=drag.scroll+drag.y-y;this.trace.scrolls++}
      if(held&&drag.kind==='step'){this.options.panel.scrollTop+=Number(drag.target?.dataset.handScroll)*dt*.65;this.trace.scrolls++}
    }
    return blocking||inCorner||Boolean(target)||Boolean(drag)
  }
  private blocking(){return this.options.isOpen()||this.options.isModal()}
  private allowed(el:HTMLElement){return !el.matches(':disabled')&&(this.options.panel.contains(el)||Boolean(el.closest('dialog[open],.tutorial-card,.camera-actions')))}
  private position(hand:HandSample){
    const rect=this.options.stage.getBoundingClientRect(),vw=this.options.video.videoWidth||16,vh=this.options.video.videoHeight||9,scale=Math.max(rect.width/vw,rect.height/vh)
    const tip=hand.landmarks[8]!,x=(rect.left+(rect.width-vw*scale)/2+tip.x*vw*scale)/innerWidth,y=(rect.top+(rect.height-vh*scale)/2+tip.y*vh*scale)/innerHeight
    return {x:Math.max(.01,Math.min(.99,(x-.08)/.84)),y:Math.max(.01,Math.min(.99,(y-.08)/.84))}
  }
}
function distance(a:Point,b:Point){return Math.hypot(a.x-b.x,a.y-b.y)}
